import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../services/api'
import { showError } from '../utils/showError'
import { extractErrorMessage } from '../utils/errorUtils'
import { 
  DetailPageLayout,
  LoadingSpinner,
  Button,
  FormSelect,
  FormTextarea,
  ErrorMessage,
  Card,
  CardHeader,
  CardBody
} from '../components/common'

const StaticAssetDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [asset, setAsset] = useState(null)
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    category: '',
    description: ''
  })

  useEffect(() => {
    loadAsset()
    loadCategories()
  }, [id])

  const loadAsset = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get(`/StaticAssets/${id}`)
      setAsset(response.data)
      setFormData({
        category: response.data.category || '',
        description: response.data.description || ''
      })
    } catch (err) {
      console.error('Failed to load asset:', err)
      setError(extractErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      const response = await api.get('/StaticAssets/categories')
      setCategories(response.data || [])
    } catch (err) {
      console.error('Failed to load categories:', err)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      await api.put(`/StaticAssets/${id}`, formData)
      setAsset(prev => ({ ...prev, ...formData }))
      setIsEditing(false)
    } catch (err) {
      console.error('Failed to save asset:', err)
      showError(err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this asset? This action cannot be undone.')) {
      try {
        await api.delete(`/StaticAssets/${id}`)
        navigate('/static-assets')
      } catch (err) {
        showError(err)
      }
    }
  }

  const handleCancel = () => {
    setFormData({
      category: asset.category || '',
      description: asset.description || ''
    })
    setIsEditing(false)
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    // Could add a toast notification here
  }

  const downloadAsset = () => {
    if (asset.url) {
      const link = document.createElement('a')
      link.href = asset.url
      link.download = asset.fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (!asset) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Asset not found</h2>
        <p className="text-gray-600 mb-4">The requested asset could not be found.</p>
        <Button
          onClick={() => navigate('/static-assets')}
          variant="primary"
        >
          Back to Assets
        </Button>
      </div>
    )
  }

  return (
    <DetailPageLayout
      title={asset.fileName}
      subtitle="Static Asset Details"
      backLink={{ label: "Back to Assets", href: "/static-assets" }}
      actions={[
        asset.url && (
          <Button
            key="download"
            onClick={downloadAsset}
            variant="secondary"
          >
            Download
          </Button>
        ),
        !isEditing && (
          <Button
            key="edit"
            onClick={() => setIsEditing(true)}
            variant="primary"
          >
            Edit
          </Button>
        ),
        <Button
          key="delete"
          onClick={handleDelete}
          variant="danger"
        >
          Delete
        </Button>
      ].filter(Boolean)}
    >
      {error && (
        <ErrorMessage 
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Asset Preview */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader title="Preview" />
            <CardBody>
            
            {asset.contentType?.startsWith('image/') && asset.url ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <img
                  src={asset.url}
                  alt={asset.fileName}
                  className="w-full h-auto max-h-96 object-contain"
                />
              </div>
            ) : asset.contentType?.includes('pdf') && asset.url ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden h-96">
                <iframe
                  src={asset.url}
                  className="w-full h-full"
                  title={asset.fileName}
                />
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-12 text-center">
                <div className="text-6xl mb-4">📎</div>
                <p className="text-gray-600">Preview not available for this file type</p>
                {asset.url && (
                  <Button
                    onClick={downloadAsset}
                    variant="ghost"
                    className="mt-4"
                  >
                    Download to view
                  </Button>
                )}
              </div>
            )}
            </CardBody>
          </Card>
        </div>

        {/* Asset Details */}
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader title="Asset Information" />
            <CardBody>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File Name
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900">{asset.fileName}</span>
                    <button
                      onClick={() => copyToClipboard(asset.fileName)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content Type
                  </label>
                  <span className="text-sm text-gray-900">{asset.contentType}</span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File Size
                  </label>
                  <span className="text-sm text-gray-900">{formatFileSize(asset.size)}</span>
                </div>

                {asset.url && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-900 truncate">{asset.url}</span>
                      <button
                        onClick={() => copyToClipboard(asset.url)}
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                        title="Copy URL to clipboard"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created
                  </label>
                  <span className="text-sm text-gray-900">
                    {new Date(asset.createdAt).toLocaleString()}
                  </span>
                </div>

                {asset.updatedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Updated
                    </label>
                    <span className="text-sm text-gray-900">
                      {new Date(asset.updatedAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Editable Metadata */}
          <Card>
            <CardHeader title="Metadata" />
            <CardBody>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  {isEditing ? (
                    <FormSelect
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      options={[
                        { value: '', label: 'Select a category' },
                        ...categories.map(category => ({ value: category, label: category }))
                      ]}
                    />
                  ) : (
                    <span className="text-sm text-gray-900">
                      {asset.category || 'No category assigned'}
                    </span>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  {isEditing ? (
                    <FormTextarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      placeholder="Add a description for this asset..."
                    />
                  ) : (
                    <span className="text-sm text-gray-900">
                      {asset.description || 'No description provided'}
                    </span>
                  )}
                </div>
              </div>

              {isEditing && (
                <div className="mt-6 flex gap-2">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    loading={saving}
                    variant="primary"
                  >
                    Save Changes
                  </Button>
                  <Button
                    onClick={handleCancel}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </DetailPageLayout>
  )
}

export default StaticAssetDetail